import { native } from '@forest/native-services'
import { cn } from '@forest/shared'
import { For, Show } from 'solid-js'
import { Countdown } from '~/components/countdown'
import { HighlightedText } from '~/components/highlighted-text'
import { PlusButton } from '~/components/plus-button'
import { Screen } from '~/components/screen'
import { createNow } from '~/lib/clock'
import {
  expiryCountdown,
  remainingDays,
  type ExpiringSubscription,
} from '~/lib/expiring-subscription'
import { lostBenefits } from '~/lib/lost-benefits'
import { m } from '~/paraglide/messages'

const daysTitle = (days: number) =>
  m.cn_android_paywall_7_day_title({ number: days })

// The design's wobble amplitude per icon, repeating down the list; each
// icon swings opposite to its neighbours.
const WOBBLE_ANGLES_RAD = [0.019, 0.021, 0.023]
const WOBBLE_SHIFT_PX = 0.25

function wobble(index: number) {
  const direction = index % 2 === 0 ? 1 : -1
  const angle = WOBBLE_ANGLES_RAD[index % WOBBLE_ANGLES_RAD.length]
  return {
    '--wobble-angle': `${direction * angle}rad`,
    '--wobble-shift': `${direction * WOBBLE_SHIFT_PX}px`,
  }
}

function Divider() {
  return <span class="h-0.5 flex-1 rounded-full bg-white/10" />
}

export function LossScreen(props: {
  subscription: ExpiringSubscription
  onRenew: () => void
}) {
  const now = createNow()
  const days = () => remainingDays(props.subscription.expiresAtMs, now())
  const countdown = () => expiryCountdown(props.subscription.expiresAtMs, now())

  return (
    <Screen
      footer={
        <>
          <PlusButton onClick={() => props.onRenew()}>
            {m.cn_android_paywall_benefit_btn()}
          </PlusButton>
          <button
            type="button"
            class="h-10 w-full rounded-xl text-base font-extrabold leading-5 [text-shadow:0_0_10px_rgba(198,255,205,0.14)] active:text-white/70"
            onClick={() => native.general.close()}
          >
            {m.no_thanks()}
          </button>
        </>
      }
    >
      <h1 class="whitespace-pre-line text-center text-[26px] font-extrabold leading-9">
        <HighlightedText
          message={
            props.subscription.isExpiringSoon
              ? m.paywall_expiring_soon_title()
              : daysTitle(days())
          }
        />
      </h1>
      <Show when={countdown()}>
        {(time) => (
          <div class="mt-5 flex items-center gap-2">
            <Divider />
            <Countdown time={time()} />
            <Divider />
          </div>
        )}
      </Show>
      <section
        class={cn(
          'gradient-stroke gradient-stroke-glass-edge flex flex-col gap-4 rounded-[32px] bg-white/10 py-6 pl-6 pr-[18px] [--stroke-width:2px]',
          countdown() ? 'mt-5' : 'mt-[34px]',
        )}
      >
        <h2 class="text-center text-base font-semibold leading-[22px]">
          {m.cn_android_paywall_benefit_lose_title()}
        </h2>
        <ul class="flex flex-col gap-4">
          <For each={lostBenefits(props.subscription.isPremium)}>
            {({ label, Icon }, index) => (
              <li class="flex items-center gap-3">
                <span
                  class="benefit-wobble flex shrink-0"
                  style={wobble(index())}
                >
                  <Icon />
                </span>
                <span class="text-sm font-semibold">{label}</span>
              </li>
            )}
          </For>
        </ul>
      </section>
    </Screen>
  )
}
